import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { MOBILE_SELECTION_BAR } from '@lib/style/breakpoints';
import {
  ACCEPTED_IMAGE_SEARCH_EXTENSIONS_AS_ACCEPT_STRING,
  isAcceptedImageFile,
} from '@lib/utils/fileExtensions';
import { visualSearchModesPhrase } from '@lib/utils/searchTypeOptions';
import { showToast } from '@lib/utils/toastmaster';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { TextField, Tooltip } from '@mui/material';
import {
  LibrarySwitch,
  emptyLibraryDisabledReason,
} from '@pages/Search/components/SearchBar/components/LibrarySwitch/LibrarySwitch';
import { debugStore } from '@stores/Debug';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { uiChromeStore } from '@stores/UIChrome';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import { useConditionalFocus } from 'hooks/useConditionalFocus';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ClipSearch } from './components/ClipSearch/ClipSearch';
import { ImageSearch } from './components/ImageSearch/ImageSearch';
import { MediaPill } from './components/MediaPill/MediaPill';
import { ResultTypeSwitch } from './components/ResultTypeSwitch/ResultTypeSwitch';
import css from './SearchBar.module.scss';

// Placeholder per search mode. Phrased as an example of what to type (a scene, a
// quote) rather than a meta-label of the search dimension — and kept honest:
// visual-only search can't match what's said, so don't offer quotes unless unified.
const SEARCH_PLACEHOLDERS = {
  visual: 'Find a scene or object…',
  transcript: 'Find a quote or topic…',
  unified: 'Find anything…',
} as const;

interface SearchBarProps {
  className?: string;
  style?: React.CSSProperties;
  focusOnMount?: boolean;
  /** When true, in the centered/empty state on desktop, render the
   *  container with the original translucent background (`rgba(30,
   *  30, 30, 0.1)`) so a hero/page background can show through.
   *  SearchPage opts in; Workspace doesn't (it wants the opaque
   *  frosted-glass-gray surface). */
  transparentWhenCentered?: boolean;
}

export const SearchBar = observer(
  ({ className, style, focusOnMount, transparentWhenCentered }: SearchBarProps = {}) => {
    const libraryManager = useLibraryManager();
    const librarySearch = libraryManager.multiLibSearch;
    const searchPlaceholder = SEARCH_PLACEHOLDERS[librarySearch.resultType];
    const uiPreferences = useUIPreferences();
    const searchInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const isSelectingMoments = librarySearch.momentSelection.isSelecting;

    // TODO: Add support for unified. Attention: API payload is different for unified.
    // Might need a refactor / big change on LibraryManagerMultiLibrarySearch.
    const imageSearchEnabled = ['visual', 'unified'].includes(librarySearch.resultType);
    const clipSearchEnabled = ['visual', 'unified'].includes(librarySearch.resultType);

    const isActive = librarySearch.hasSearchTerms;

    // On mobile, sync the fixed search bar position with the placeholder
    // when not active, then animate to above the nav when active
    const [naturalBottom, setNaturalBottom] = useState<number | null>(null);
    useEffect(() => {
      if (!isMobile || !placeholderRef.current) return;
      const updatePosition = () => {
        const rect = placeholderRef.current?.getBoundingClientRect();
        if (rect) {
          setNaturalBottom(window.innerHeight - rect.bottom);
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }, [isMobile, isActive]);

    // Measure the bar's rect and publish to uiChromeStore so the toaster can
    // align with it. Triggered only when the bar's geometry can actually
    // change: state deps that affect positioning, window resize,
    // ResizeObserver (size), and transitionend filtered to position
    // properties only — descendants' transitions (hovers, colour fades)
    // bubble to the container too, so an unfiltered listener thrashes layout
    // on every micro-interaction.
    const publishSearchBarRect = useCallback(() => {
      // Mobile keeps the top-center toaster (no SearchBar tracking), so skip
      // the layout read entirely on mobile to avoid wasted measurement work.
      if (isMobile) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      uiChromeStore.setSearchBarRect(viewportH - rect.top, rect.left);
    }, [isMobile]);

    useLayoutEffect(() => {
      publishSearchBarRect();
      // `naturalBottom` is mobile-only and we no-op on mobile, so it isn't a
      // dep here — the publish path's own `isMobile` guard handles it.
    }, [publishSearchBarRect, isActive, isInputFocused]);

    useEffect(() => {
      window.addEventListener('resize', publishSearchBarRect);
      const el = containerRef.current;
      let ro: ResizeObserver | undefined;
      const onTransitionEnd = (e: TransitionEvent) => {
        // Filter to properties that actually move the bar; ignore descendants'
        // colour/opacity/etc. transitions that bubble up.
        if (
          e.target !== el ||
          (e.propertyName !== 'bottom' &&
            e.propertyName !== 'top' &&
            e.propertyName !== 'left' &&
            e.propertyName !== 'right' &&
            e.propertyName !== 'transform')
        ) {
          return;
        }
        publishSearchBarRect();
      };
      if (el) {
        ro = new ResizeObserver(publishSearchBarRect);
        ro.observe(el);
        el.addEventListener('transitionend', onTransitionEnd);
      }
      return () => {
        window.removeEventListener('resize', publishSearchBarRect);
        ro?.disconnect();
        el?.removeEventListener('transitionend', onTransitionEnd);
        uiChromeStore.clearSearchBarRect();
      };
    }, [publishSearchBarRect]);

    // Autofocus the searchbar when there are no search terms
    const shouldAutofocus = !librarySearch.hasSearchTerms;
    useConditionalFocus(searchInputRef, shouldAutofocus, false);

    // When focusOnMount is set, focus and place cursor at end of existing text
    useEffect(() => {
      if (!focusOnMount) return;
      // Use rAF to ensure the DOM is fully ready after navigation
      requestAnimationFrame(() => {
        const input = searchInputRef.current;
        if (input) {
          input.focus();
          const len = input.value.length;
          input.setSelectionRange(len, len);
        }
      });
    }, [focusOnMount]);

    // Register focus callback with UIPreferences
    useEffect(() => {
      const focusCallback = () => {
        const input = searchInputRef.current;
        if (input) {
          input.focus();
          input.select();
        }
      };

      uiPreferences.setSearchBarFocusCallback(focusCallback);

      return () => {
        uiPreferences.setSearchBarFocusCallback(null);
      };
    }, [uiPreferences]);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        const validImageFiles = files.filter(isAcceptedImageFile);

        if (files.length > 0 && validImageFiles.length === 0) {
          showToast('unsupported-file-type');
          e.target.value = '';
          return;
        }

        // No need for a multiple-images toast here: the <input> lacks the
        // `multiple` attribute, so the browser only ever provides one file.
        if (validImageFiles.length > 0) {
          librarySearch.setSearchImage(validImageFiles[0]);
          trackingStore.searchImageAdded('upload');
        }

        // Reset so the same file can be re-selected
        e.target.value = '';
      },
      [librarySearch],
    );

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);

      const files = Array.from(e.dataTransfer.files);
      const validImageFiles = files.filter(isAcceptedImageFile);

      if (files.length > 0 && validImageFiles.length === 0) {
        // User dropped non-image files or SVG files
        showToast(!imageSearchEnabled ? 'file-drop-rejected' : 'unsupported-file-type');
        return;
      }

      if (!imageSearchEnabled) {
        showToast('image-drop-rejected');
        return;
      }

      if (validImageFiles.length > 1) {
        // User dropped multiple images
        showToast('multiple-images-detected');
      }

      if (validImageFiles.length > 0) {
        // Use the first valid image
        librarySearch.setSearchImage(validImageFiles[0]);
        trackingStore.searchImageAdded('drag-drop');
      }
    };

    const mobileActive = isActive || (isMobile && isInputFocused);
    // When selecting moments on mobile, scoot search bar up to make room for selection bar
    const mobileSelectionOffset =
      isMobile && isSelectingMoments ? MOBILE_SELECTION_BAR.height + MOBILE_SELECTION_BAR.gap : 0;
    const mobileBottom = (mobileActive ? 72 : (naturalBottom ?? 72)) + mobileSelectionOffset;

    return (
      <>
        {isMobile && !isActive && (
          <div ref={placeholderRef} className={css.mobileSearchBarPlaceholder} />
        )}
        <div
          ref={containerRef}
          className={classNames(
            css.container,
            debugStore.bigSearchBar ? css.bigSearchBar : '',
            mobileActive ? css.active : '',
            !isMobile && !isActive && transparentWhenCentered && css.desktopCentered,
            isDraggingOver && imageSearchEnabled ? css.draggingOver : '',
            className,
          )}
          style={{
            ...style,
            ...(isMobile ? { bottom: mobileBottom } : {}),
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={css.innerContainer}>
            {isMobile ? (
              <>
                {/* Empty libraries are shown but not selectable: searching one can only
                    return nothing, which reads as a broken search rather than an empty
                    library. The row says why. */}
                <LibrarySwitch disabledReasonFor={emptyLibraryDisabledReason} />
                <div className={css.searchArea}>
                  <TextField
                    className={css.userInput}
                    placeholder={searchPlaceholder}
                    inputRef={searchInputRef}
                    value={libraryManager.multiLibSearch.request?.text ?? ''}
                    onChange={(e) => {
                      libraryManager.multiLibSearch.setSearchText(e.target.value);
                    }}
                    onFocus={() => {
                      setIsInputFocused(true);
                      searchInputRef.current?.select();
                      // Wait for keyboard to open, then scroll search bar
                      // to the bottom of the visible area (just above keyboard)
                      setTimeout(() => {
                        searchInputRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'end',
                        });
                      }, 300);
                    }}
                    onBlur={() => {
                      setIsInputFocused(false);
                    }}
                  />
                </div>
                <ResultTypeSwitch />
              </>
            ) : (
              <>
                <Tooltip title="Collapse search" placement="top" arrow>
                  <button
                    className={classNames(css.collapseContainer, {
                      [css.hidden]:
                        !uiPreferences.showSearchBarWhileSelectingMoments || !isSelectingMoments,
                    })}
                    onClick={() => {
                      trackingStore.collapseSearchClicked();
                      uiPreferences.hideSearchBarWhileSelectingMoments();
                    }}
                  >
                    <KeyboardArrowDownIcon />
                  </button>
                </Tooltip>
                <div className={css.searchArea}>
                  {imageSearchEnabled && librarySearch.request?.image && <ImageSearch />}
                  {clipSearchEnabled && librarySearch.request?.clipId && <ClipSearch />}
                  <TextField
                    className={classNames(css.userInput, {
                      [css.bigSearchBarInput]: debugStore.bigSearchBar,
                    })}
                    multiline
                    rows={2}
                    placeholder={searchPlaceholder}
                    inputRef={searchInputRef}
                    value={libraryManager.multiLibSearch.request?.text ?? ''}
                    onChange={(e) => {
                      libraryManager.multiLibSearch.setSearchText(e.target.value);
                    }}
                    onFocus={() => {
                      setIsInputFocused(true);
                      searchInputRef.current?.select();
                    }}
                    onBlur={() => {
                      setIsInputFocused(false);
                    }}
                    onKeyDown={(e) => {
                      // If text field is empty and backspace is pressed, remove search queries
                      const textValue = libraryManager.multiLibSearch.request?.text ?? '';
                      if (e.key === 'Backspace' && textValue === '' && isInputFocused) {
                        // First backspace: remove CLIP search query if it exists
                        if (librarySearch.request?.clipId) {
                          e.preventDefault();
                          librarySearch.setSearchClip(null, null, false);
                          return;
                        }
                        // Second backspace: remove IMAGE search query if it exists
                        if (librarySearch.request?.image) {
                          e.preventDefault();
                          librarySearch.setSearchImage(null);
                          return;
                        }
                      }
                    }}
                    onPaste={async (e) => {
                      const files = Array.prototype.slice.apply(e.clipboardData.files) as File[];
                      const image = files.filter(isAcceptedImageFile)[0];
                      if (!image && files.length > 0) {
                        showToast(
                          !imageSearchEnabled ? 'file-paste-rejected' : 'unsupported-file-type',
                        );
                        return;
                      }
                      if (image) {
                        if (!imageSearchEnabled) {
                          showToast('image-paste-rejected');
                          return;
                        }
                        librarySearch.setSearchImage(image);
                        trackingStore.searchImageAdded('paste');
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
                <div className={css.controlsContainer}>
                  {/* See the mobile branch above for why empty libraries are disabled. */}
                  <LibrarySwitch disabledReasonFor={emptyLibraryDisabledReason} />
                  <Tooltip
                    title={
                      !imageSearchEnabled
                        ? librarySearch.allScopeIsTranscriptOnly
                          ? // Visual modes are permanently unavailable here, so don't tell the
                            // user to "switch modes" — explain the library is transcript-only.
                            `Image search needs ${visualSearchModesPhrase()}, which isn’t available for this library.`
                          : `Adding image queries is disabled in transcript mode. Please switch to ${visualSearchModesPhrase()} mode to search with image queries.`
                        : librarySearch.request?.image
                          ? 'Click here to replace your current image search query. Drag and drop and copy-paste works too!'
                          : 'Add an image to your search query to find visually similar moments. Drag and drop and copy-paste works too!'
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <button
                        className={css.pill}
                        onClick={() => {
                          trackingStore.searchImageButtonClicked();
                          fileInputRef.current?.click();
                        }}
                        type="button"
                        disabled={!imageSearchEnabled}
                      >
                        <ImageSearchIcon
                          sx={{ width: 22, height: 22, color: 'rgba(193, 193, 194, 1)' }}
                        />
                      </button>
                    </span>
                  </Tooltip>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_SEARCH_EXTENSIONS_AS_ACCEPT_STRING}
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                  />
                  {libraryManager.multiLibSearch.mediaId && <MediaPill />}
                  <div style={{ marginLeft: 'auto' }}>
                    <ResultTypeSwitch />
                  </div>
                  <Tooltip title="Just type in your search terms" arrow placement="top">
                    <div className={css.pill}>
                      <SearchIcon sx={{ width: 22, height: 22, color: 'rgba(193, 193, 194, 1)' }} />
                    </div>
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  },
);
